/**
 * 📸 STORIES - "Je suis là" - Contenu éphémère 24h
 * 
 * Stories géolocalisées visibles par les gens du quartier
 * Inspiration: Instagram Stories + Snapchat Maps
 * 
 * Features:
 * - Création de story (photo/texte/mood)
 * - Feed de stories du quartier
 * - Réactions et réponses
 * - Expiration automatique 24h
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, MapPin, Clock, Heart, MessageCircle, 
  X, ChevronLeft, ChevronRight, Plus, Sparkles,
  Eye, Send, Image, Smile, Coffee, Music, Zap
} from 'lucide-react';
import { Button } from '../../../ui/button';
import { toast } from 'sonner';

// 🌈 Moods disponibles pour les stories
const STORY_MOODS = [
  { id: 'cafe', emoji: '☕', label: 'Café time', color: '#8B4513' },
  { id: 'drink', emoji: '🍷', label: 'Apéro', color: '#722F37' },
  { id: 'food', emoji: '🍕', label: 'Je mange', color: '#FF6347' },
  { id: 'sport', emoji: '🏋️', label: 'Sport', color: '#32CD32' },
  { id: 'chill', emoji: '😌', label: 'Chill', color: '#87CEEB' },
  { id: 'party', emoji: '🎉', label: 'Soirée', color: '#FF1493' },
  { id: 'work', emoji: '💻', label: 'Taf', color: '#4169E1' },
  { id: 'bored', emoji: '😴', label: 'Je m\'ennuie', color: '#808080' },
  { id: 'adventure', emoji: '🌍', label: 'Aventure', color: '#228B22' },
  { id: 'love', emoji: '💜', label: 'Mood love', color: '#FF69B4' },
  { id: 'music', emoji: '🎵', label: 'Musique', color: '#9932CC' },
  { id: 'shopping', emoji: '🛍️', label: 'Shopping', color: '#FFD700' }
];

// 📸 Composant principal - Feed de stories
export function StoriesFeed({ stories = [], currentUserId, onStoryClick, onCreateStory, isDarkMode }) {
  const scrollRef = useRef(null);
  
  // Filtrer les stories non expirées (< 24h)
  const activeStories = stories.filter(story => {
    const createdAt = new Date(story.createdAt);
    const now = new Date();
    return (now - createdAt) < 24 * 60 * 60 * 1000;
  });
  
  // Grouper par utilisateur
  const groupedStories = activeStories.reduce((acc, story) => {
    if (!acc[story.userId]) {
      acc[story.userId] = {
        user: story.user,
        stories: [],
        hasUnread: false
      };
    }
    acc[story.userId].stories.push(story);
    if (!story.viewedBy?.includes(currentUserId)) {
      acc[story.userId].hasUnread = true;
    }
    return acc;
  }, {});
  
  return (
    <div className={`rounded-2xl p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">📸</span>
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Stories du quartier
          </h3>
          <span className={`text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-pink-500/20 text-pink-300' : 'bg-pink-100 text-pink-600'}`}>
            {Object.keys(groupedStories).length} actives
          </span>
        </div>
        <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          <Clock className="w-3 h-3 inline mr-1" />
          Expire en 24h
        </span>
      </div>
      
      {/* Stories carousel */}
      <div 
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {/* Bouton créer une story */}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onCreateStory}
          className="flex-shrink-0 w-20 flex flex-col items-center gap-1"
        >
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center border-2 border-dashed ${
            isDarkMode 
              ? 'border-pink-500 bg-pink-500/10' 
              : 'border-pink-400 bg-pink-50'
          }`}>
            <Plus className="w-8 h-8 text-pink-500" />
          </div>
          <span className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Ta story
          </span>
        </motion.button>
        
        {/* Stories des autres */}
        {Object.entries(groupedStories).map(([userId, data]) => (
          <motion.button
            key={userId}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onStoryClick && onStoryClick(userId, data.stories)}
            className="flex-shrink-0 w-20 flex flex-col items-center gap-1"
          >
            <div className={`relative w-16 h-16 rounded-2xl overflow-hidden ${
              data.hasUnread 
                ? 'ring-2 ring-pink-500 ring-offset-2' 
                : 'ring-1 ring-gray-300'
            } ${isDarkMode ? 'ring-offset-gray-800' : 'ring-offset-white'}`}>
              {data.user?.avatar ? (
                <img 
                  src={data.user.avatar} 
                  alt={data.user.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center text-white text-xl font-bold">
                  {data.user?.name?.[0] || '?'}
                </div>
              )}
              
              {/* Mood badge */}
              {data.stories[0]?.mood && (
                <div className="absolute bottom-0 right-0 w-6 h-6 rounded-full bg-white shadow flex items-center justify-center text-sm">
                  {STORY_MOODS.find(m => m.id === data.stories[0].mood)?.emoji || '💫'}
                </div>
              )}
            </div>
            <span className={`text-xs font-medium truncate max-w-full ${
              data.hasUnread 
                ? isDarkMode ? 'text-white' : 'text-gray-900'
                : isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {data.user?.name?.split(' ')[0] || 'Anonyme'}
            </span>
          </motion.button>
        ))}
        
        {/* Message si vide */}
        {Object.keys(groupedStories).length === 0 && (
          <div className={`flex-1 py-8 text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Aucune story pour le moment</p>
            <p className="text-xs">Sois le premier à partager ! 😊</p>
          </div>
        )}
      </div>
    </div>
  );
}

// 🎨 Créateur de story
export function StoryCreator({ onClose, onPublish, isDarkMode }) {
  const [step, setStep] = useState('mood'); // mood -> content -> location -> preview
  const [selectedMood, setSelectedMood] = useState(null);
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState(null);
  const [location, setLocation] = useState(null);
  const fileInputRef = useRef(null);
  
  // 📍 Obtenir la localisation
  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setLocation({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          });
          toast.success('📍 Position détectée !');
        },
        () => toast.error('Impossible de détecter ta position')
      );
    }
  };
  
  // 📸 Gérer l'upload de photo
  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => setPhoto(reader.result);
      reader.readAsDataURL(file);
    }
  };
  
  // 🚀 Publier la story
  const handlePublish = () => {
    if (!selectedMood) {
      toast.error('Choisis un mood !');
      return;
    }
    
    onPublish({
      mood: selectedMood.id,
      text,
      photo,
      location,
      createdAt: new Date().toISOString()
    });
    
    toast.success('📸 Story publiée ! Visible pendant 24h ✨');
    onClose();
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className={`w-full max-w-md rounded-3xl overflow-hidden ${
          isDarkMode ? 'bg-gray-900' : 'bg-white'
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <button onClick={onClose}>
            <X className={`w-6 h-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          </button>
          <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            📸 Nouvelle Story
          </h3>
          <Button
            size="sm"
            onClick={handlePublish}
            disabled={!selectedMood}
            className="bg-gradient-to-r from-pink-500 to-purple-500 text-white"
          >
            Publier
          </Button>
        </div>
        
        <div className="p-4 space-y-6">
          {/* Mood selector */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              🌈 C'est quoi ton mood ?
            </label>
            <div className="grid grid-cols-4 gap-2">
              {STORY_MOODS.map((mood) => (
                <motion.button
                  key={mood.id}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setSelectedMood(mood)}
                  className={`p-3 rounded-xl flex flex-col items-center gap-1 transition-all ${
                    selectedMood?.id === mood.id
                      ? 'ring-2 ring-pink-500 bg-pink-50 dark:bg-pink-500/20'
                      : isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <span className="text-2xl">{mood.emoji}</span>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {mood.label}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
          
          {/* Texte */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              ✍️ Un petit mot ? (optionnel)
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Ex: Qui veut me rejoindre ? 😊"
              maxLength={150}
              rows={2}
              className={`w-full p-3 rounded-xl resize-none ${
                isDarkMode 
                  ? 'bg-gray-800 text-white border-gray-700' 
                  : 'bg-gray-100 text-gray-900 border-gray-200'
              } border focus:ring-2 focus:ring-pink-500`}
            />
            <p className={`text-xs text-right mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {text.length}/150
            </p>
          </div>
          
          {/* Photo (optionnel) */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              📷 Ajoute une photo (optionnel)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
            {photo ? (
              <div className="relative">
                <img 
                  src={photo} 
                  alt="Preview" 
                  className="w-full h-40 object-cover rounded-xl"
                />
                <button
                  onClick={() => setPhoto(null)}
                  className="absolute top-2 right-2 p-1 bg-black/50 rounded-full"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileInputRef.current?.click()}
                className={`w-full p-4 rounded-xl border-2 border-dashed flex items-center justify-center gap-2 ${
                  isDarkMode 
                    ? 'border-gray-700 text-gray-400 hover:border-pink-500 hover:text-pink-400' 
                    : 'border-gray-300 text-gray-500 hover:border-pink-500 hover:text-pink-500'
                } transition-colors`}
              >
                <Image className="w-5 h-5" />
                <span>Ajouter une photo</span>
              </button>
            )}
          </div>
          
          {/* Location */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className={`w-5 h-5 ${location ? 'text-green-500' : isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {location ? 'Position détectée ✓' : 'Ajouter ma position'}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={getLocation}
              disabled={!!location}
            >
              {location ? '✓' : 'Détecter'}
            </Button>
          </div>
        </div>
        
        {/* Footer info */}
        <div className={`p-4 text-center border-t ${isDarkMode ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            ⏰ Ta story sera visible pendant 24h par les gens du quartier
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

// 👁️ Viewer de story
export function StoryViewer({ stories, initialIndex = 0, onClose, onReact, currentUserId, isDarkMode }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [reply, setReply] = useState('');
  
  const currentStory = stories[currentIndex];
  
  // Auto-progress (5 secondes par story)
  useEffect(() => {
    if (isPaused) return;
    
    const duration = 5000;
    const interval = 50;
    const increment = (interval / duration) * 100;
    
    const timer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          // Passer à la story suivante
          if (currentIndex < stories.length - 1) {
            setCurrentIndex(currentIndex + 1);
            return 0;
          } else {
            onClose();
            return prev;
          }
        }
        return prev + increment;
      });
    }, interval);
    
    return () => clearInterval(timer);
  }, [currentIndex, isPaused, stories.length, onClose]);
  
  // Reset progress quand on change de story
  useEffect(() => {
    setProgress(0);
  }, [currentIndex]);
  
  const goNext = () => {
    if (currentIndex < stories.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      onClose();
    }
  };
  
  const goPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };
  
  const mood = STORY_MOODS.find(m => m.id === currentStory?.mood);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black flex items-center justify-center"
      onClick={onClose}
    >
      <div 
        className="relative w-full max-w-md h-full max-h-[90vh] rounded-2xl overflow-x-hidden overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={() => setIsPaused(true)}
        onMouseUp={() => setIsPaused(false)}
        onTouchStart={() => setIsPaused(true)}
        onTouchEnd={() => setIsPaused(false)}
      >
        {/* Background */}
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: mood?.color || '#1a1a1a' }}
        >
          {currentStory?.photo && (
            <img 
              src={currentStory.photo} 
              alt="" 
              className="w-full h-full object-cover opacity-90"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/70" />
        </div>
        
        {/* Progress bars */}
        <div className="absolute top-4 left-4 right-4 flex gap-1">
          {stories.map((_, idx) => (
            <div 
              key={idx}
              className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
            >
              <div 
                className="h-full bg-white transition-all"
                style={{ 
                  width: idx < currentIndex ? '100%' : idx === currentIndex ? `${progress}%` : '0%' 
                }}
              />
            </div>
          ))}
        </div>
        
        {/* Header */}
        <div className="absolute top-8 left-4 right-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl">
              {currentStory?.user?.avatar ? (
                <img src={currentStory.user.avatar} alt="" className="w-full h-full rounded-full object-cover" />
              ) : (
                mood?.emoji || '💫'
              )}
            </div>
            <div>
              <p className="text-white font-bold text-sm">
                {currentStory?.user?.name || 'Anonyme'}
              </p>
              <p className="text-white/70 text-xs flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {currentStory?.createdAt && new Date(currentStory.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                {currentStory?.location && (
                  <><MapPin className="w-3 h-3 ml-2" /> Près de toi</>
                )}
              </p>
            </div>
          </div>
          <button onClick={onClose}>
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
        
        {/* Content */}
        <div className="absolute inset-0 flex items-center justify-center p-8">
          <div className="text-center">
            <span className="text-6xl mb-4 block">{mood?.emoji}</span>
            <p className="text-2xl font-bold text-white mb-2">{mood?.label}</p>
            {currentStory?.text && (
              <p className="text-white/90 text-lg">{currentStory.text}</p>
            )}
          </div>
        </div>
        
        {/* Navigation zones */}
        <div className="absolute inset-y-0 left-0 w-1/3" onClick={goPrev} />
        <div className="absolute inset-y-0 right-0 w-1/3" onClick={goNext} />
        
        {/* Reply input */}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Répondre..."
              className="flex-1 px-4 py-2 rounded-full bg-white/20 text-white placeholder-white/50 backdrop-blur-sm border border-white/20 focus:outline-none focus:border-white/50"
            />
            <button className="p-2 rounded-full bg-white/20 hover:bg-white/30">
              <Heart className="w-5 h-5 text-white" />
            </button>
            <button className="p-2 rounded-full bg-pink-500 hover:bg-pink-600">
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default StoriesFeed;
